import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../api";
import { ArrowLeft, ShieldCheck, X } from "lucide-react";

export default function LMSAssessment() {
  const { assessmentId } = useParams();
  const nav = useNavigate();
  const [asmt, setAsmt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get(`/lms/assessments/${assessmentId}`).then(r => setAsmt(r.data)).catch(e => setMsg(formatError(e)));
  }, [assessmentId]);

  const submit = async () => {
    setMsg("");
    if (!asmt) return;
    if (Object.keys(answers).length !== asmt.questions.length) {
      setMsg("Please answer every question."); return;
    }
    try {
      const arr = asmt.questions.map((_, i) => answers[i]);
      const r = await api.post(`/lms/assessments/${assessmentId}/submit`, { answers: arr });
      setResult(r.data);
    } catch (e) { setMsg(formatError(e)); }
  };

  if (!asmt) return <div className="min-h-screen bg-paper p-10 text-muted">{msg || "Loading…"}</div>;

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-rule bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-signal" data-testid="assessment-back">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="eyebrow">Pass {asmt.pass_score}%</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display font-extrabold text-3xl" data-testid="assessment-title">{asmt.title}</h1>
        <p className="text-muted mt-2">Auto-graded · {asmt.questions.length} questions</p>

        {result ? (
          <div className="card card-pad mt-8 text-center" data-testid="assessment-result">
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${result.passed ? "bg-signal text-white" : "bg-rule"}`}>
              {result.passed ? <ShieldCheck size={32} /> : <X size={32} />}
            </div>
            <div className="font-display font-extrabold text-5xl">{result.score}%</div>
            <p className="text-muted mt-2">{result.correct} of {result.total} correct</p>
            <p className={`mt-4 font-semibold ${result.passed ? "text-signal" : "text-red-600"}`} data-testid="assessment-verdict">
              {result.passed ? "You passed!" : `Need ${result.pass_score}% — try again.`}
            </p>
            {result.lab_completed && result.certificate && (
              <div className="mt-6 p-5 border border-signal rounded-sm bg-paper" data-testid="cert-issued">
                <div className="eyebrow text-signal">Certificate issued</div>
                <div className="font-mono text-sm mt-1">{result.certificate.cert_no}</div>
                <Link to="/student/lms/certificates" className="btn-primary mt-3 inline-flex">View certificate</Link>
              </div>
            )}
            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-ghost" data-testid="retake-btn">Retake</button>
              <Link to="/student/lms" className="btn-secondary">Back to my labs</Link>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-5 mt-8">
              {asmt.questions.map((q, i) => (
                <div key={`q-${i}-${q.q.slice(0, 40)}`} className="card card-pad" data-testid={`q-${i}`}>
                  <div className="eyebrow">Question {i + 1}</div>
                  <h3 className="font-display font-semibold text-lg mt-1">{q.q}</h3>
                  <div className="mt-4 space-y-2">
                    {q.options.map((opt, j) => (
                      <label key={`q-${i}-opt-${opt}`}
                        className={`flex items-center gap-3 px-4 py-3 border rounded-sm cursor-pointer transition ${answers[i] === j ? "border-signal bg-signal/5" : "border-rule hover:border-signal"}`}
                        data-testid={`q-${i}-opt-${j}`}>
                        <input type="radio" name={`q${i}`} className="accent-signal"
                          checked={answers[i] === j} onChange={() => setAnswers(a => ({ ...a, [i]: j }))} />
                        <span className="text-sm">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <div className="text-sm text-red-600" data-testid="assessment-msg">{msg}</div>
              <button onClick={submit} className="btn-primary" data-testid="submit-assessment-btn">Submit answers</button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
